import { inject, Injectable } from "@angular/core";
import { DateAdapter } from "@angular/material/core";
import { AVAILABLE_LOCALES } from "@config/locales/locales.config";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { Settings } from "luxon";
import { asyncScheduler, BehaviorSubject, map, Observable, scheduled, tap } from "rxjs";

type Locale = string;

export const DEFAULT_LANGUAGE_KEY = "i18nDefaultLanguage";

@Injectable({
    providedIn: "root",
})
export class LanguagesService {
    availableLocales = inject(AVAILABLE_LOCALES);
    translateService = inject(TranslateService);
    dateAdapter = inject(DateAdapter);

    public readonly currentLang$ = new BehaviorSubject<string>(this.translateService.currentLang);

    constructor(
    ) {
        this.currentLang$.next(this.translateService.currentLang);
        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.currentLang$.next(event.lang);
        });
    }

    private getLocalStorageLocale(): Locale | null {
        return localStorage.getItem(DEFAULT_LANGUAGE_KEY);
    }

    private setLocalStorageLocale(locale: Locale): void {
        localStorage.setItem(DEFAULT_LANGUAGE_KEY, locale);
    }

    public getAvailableLocales(): Observable<Locale[]> {
        return scheduled([this.availableLocales], asyncScheduler);
    }

    public getDefaultLocale(locales: Locale[]): Locale {
        const localStorageLocale = this.getLocalStorageLocale();
        if (localStorageLocale && locales.includes(localStorageLocale)) {
            return localStorageLocale;
        } else {
            return locales[0];
        }
    }

    public setLanguage(locale: string) {
        this.currentLang$.next(locale);
        this.translateService.use(locale);
        this.dateAdapter.setLocale(locale);
        this.setLocalStorageLocale(locale);
        Settings.defaultLocale = locale;
    }

    public init() {
        this.getAvailableLocales()
            .pipe(
                tap(languages => this.translateService.addLangs(languages)),
                map(languages => this.getDefaultLocale(languages)),
            )
            .subscribe({
                next: (defaultLocale) => {
                    this.translateService.setDefaultLang(defaultLocale);
                    this.setLanguage(defaultLocale);
                },
            });
    }
}
